import { useEffect, useState } from "react";
import { Card } from "../../../components/Card";
import { Switch } from "../../../components/Switch";
import { Tooltip } from "../../../components/tooltip";
import type { MultiplierConfig } from "../types";
import { SaveConfigModal } from "./SaveConfigModal";
import { ResetConfigModal } from "./ResetConfigModal";

const DEFAULT_MAX_STALE_DAYS = 7;
const DEFAULT_RESISTANCE_FACTOR = 0.5;

function FieldLabel({
  label,
  tooltipId,
  tooltipText,
}: {
  label: string;
  tooltipId?: string;
  tooltipText?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-sm text-content-primary whitespace-nowrap">
        {label}
      </span>
      {tooltipId && tooltipText && (
        <Tooltip id={tooltipId}>
          <p className="text-xs">{tooltipText}</p>
        </Tooltip>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  tooltipId,
  tooltipText,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  tooltipId?: string;
  tooltipText?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <FieldLabel
        label={label}
        tooltipId={tooltipId}
        tooltipText={tooltipText}
      />
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-content-tertiary whitespace-nowrap">
          {min}–{max}
        </span>
        <div className="relative">
          <input
            type="number"
            value={value}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
            }}
            min={min}
            max={max}
            step={step}
            className={`w-[80px] px-2 py-1.5 border border-border-primary rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary ${suffix ? "pr-6" : ""}`}
          />
          {suffix && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-content-tertiary pointer-events-none">
              {suffix}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function NullableNumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  defaultValue,
  tooltipId,
  tooltipText,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min: number;
  max: number;
  step?: number;
  defaultValue: number;
  tooltipId?: string;
  tooltipText?: string;
}) {
  const enabled = value !== null;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? defaultValue : null)}
          className="accent-primary"
        />
        <FieldLabel
          label={label}
          tooltipId={tooltipId}
          tooltipText={tooltipText}
        />
      </div>
      {enabled && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-content-tertiary whitespace-nowrap">
            {min}–{max}
          </span>
          <input
            type="number"
            value={value}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
            }}
            min={min}
            max={max}
            step={step}
            className="w-[80px] px-2 py-1.5 border border-border-primary rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      )}
    </div>
  );
}

function SwitchField({
  label,
  checked,
  onChange,
  tooltipId,
  tooltipText,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  tooltipId?: string;
  tooltipText?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <FieldLabel
        label={label}
        tooltipId={tooltipId}
        tooltipText={tooltipText}
      />
      <Switch
        checked={checked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.checked)
        }
      />
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h4 className="text-xs font-semibold text-content-tertiary uppercase tracking-wide mt-4 mb-2 first:mt-0">
      {title}
    </h4>
  );
}

function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => deepEqual(a[key], b[key]));
}

export function BaseConfigCard({
  config,
  userId,
}: {
  config: MultiplierConfig;
  userId: string;
}) {
  const [form, setForm] = useState<MultiplierConfig>(config);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    setForm(config);
  }, [config]);

  const isDirty = !deepEqual(form, config);

  const updateField = <K extends keyof MultiplierConfig>(
    key: K,
    value: MultiplierConfig[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateNested = (
    section: "competitive" | "resistance" | "events" | "differentials",
    key: string,
    value: unknown,
  ) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  return (
    <>
      <Card className="!p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-content-primary">
            Base Configuration
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowResetModal(true)}
              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
            >
              Reset to Defaults
            </button>
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={!isDirty}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
            >
              Save Changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          <div>
            <SectionHeader title="General" />
            <NumberField
              label="Aggressiveness"
              value={form.aggressiveness}
              onChange={(v) => updateField("aggressiveness", v)}
              min={1}
              max={10}
              tooltipId="tt-aggressiveness"
              tooltipText="How aggressively the algorithm adjusts prices. 1 = conservative, 10 = maximum price swings."
            />

            <SectionHeader title="Competitive" />
            <div className="flex flex-col gap-2.5">
              <NumberField
                label="Influence"
                value={form.competitive.influence}
                onChange={(v) => updateNested("competitive", "influence", v)}
                min={0}
                max={2}
                step={0.1}
                tooltipId="tt-comp-influence"
                tooltipText="Weight given to competitor pricing. 0 = ignore competitors, 2 = strong influence on price decisions."
              />
              <NumberField
                label="Position Offset"
                value={form.competitive.positionOffset}
                onChange={(v) =>
                  updateNested("competitive", "positionOffset", v)
                }
                min={-10}
                max={15}
                suffix="%"
                tooltipId="tt-comp-offset"
                tooltipText="Target price position relative to competitors. Negative = undercut, positive = price above competitors."
              />
              <NumberField
                label="Min Competitors"
                value={form.competitive.minCompetitors}
                onChange={(v) =>
                  updateNested("competitive", "minCompetitors", v)
                }
                min={1}
                max={10}
                tooltipId="tt-comp-min"
                tooltipText="Minimum number of competitor data points required before competitive pricing activates."
              />
              <NullableNumberField
                label="Max Stale Days"
                value={form.competitive.maxStaleDays}
                onChange={(v) =>
                  updateNested("competitive", "maxStaleDays", v)
                }
                min={1}
                max={14}
                defaultValue={DEFAULT_MAX_STALE_DAYS}
                tooltipId="tt-comp-stale"
                tooltipText="Ignore competitor data older than this many days. Disable to use all available data regardless of age."
              />
              <SwitchField
                label="Fade with Urgency"
                checked={form.competitive.fadeWithUrgency}
                onChange={(v) =>
                  updateNested("competitive", "fadeWithUrgency", v)
                }
                tooltipId="tt-comp-fade"
                tooltipText="Gradually reduce competitive influence as the check-in date approaches, prioritizing occupancy over positioning."
              />
              <SwitchField
                label="Close-in Amplifier"
                checked={form.competitive.closeInAmplifier}
                onChange={(v) =>
                  updateNested("competitive", "closeInAmplifier", v)
                }
                tooltipId="tt-comp-closein"
                tooltipText="Amplify price adjustments for near-term dates (within 1-3 days) to maximize last-minute revenue."
              />
            </div>
          </div>

          <div>
            <SectionHeader title="Resistance" />
            <NullableNumberField
              label="Factor"
              value={form.resistance.factor}
              onChange={(v) => updateNested("resistance", "factor", v)}
              min={0}
              max={1}
              step={0.1}
              defaultValue={DEFAULT_RESISTANCE_FACTOR}
              tooltipId="tt-resist-factor"
              tooltipText="Resistance to large price swings between consecutive updates. 0 = no smoothing, 1 = maximum smoothing."
            />

            <SectionHeader title="Events" />
            <NumberField
              label="Responsiveness"
              value={form.events.responsiveness}
              onChange={(v) => updateNested("events", "responsiveness", v)}
              min={0.5}
              max={3}
              step={0.1}
              tooltipId="tt-events-resp"
              tooltipText="How quickly prices react to detected event demand. Higher values produce larger price increases for events."
            />

            <SectionHeader title="Differentials" />
            <NumberField
              label="Responsiveness"
              value={form.differentials.responsiveness}
              onChange={(v) =>
                updateNested("differentials", "responsiveness", v)
              }
              min={0.5}
              max={3}
              step={0.1}
              tooltipId="tt-diff-resp"
              tooltipText="How quickly prices adjust based on room type demand differences. Higher values widen the spread between room categories."
            />
          </div>
        </div>
      </Card>

      {showSaveModal && (
        <SaveConfigModal
          originalConfig={config}
          editedConfig={form}
          userId={userId}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      {showResetModal && (
        <ResetConfigModal
          userId={userId}
          onClose={() => setShowResetModal(false)}
        />
      )}
    </>
  );
}
